import { UserModel } from '../interfaces/models/userModel';
import { callApi } from './api/api';
import { Auth } from 'aws-amplify';
import { setRecoil, getRecoil } from 'recoil-nexus';
import { userModelState } from '../state/atoms/userModel';
import { ipState } from '../state/atoms/ip';

/**
 * HYBRID CURRENCY DETECTION SYSTEM
 * 
 * This system automatically initializes on app load and uses multiple detection methods:
 * 1. User's stored preference (highest priority)
 * 2. IP geolocation (cached result, most accurate for location)
 * 3. Basic browser locale detection (fallback)
 * 4. Enhanced browser detection (timezone + multiple language signals)
 * 5. Default AUD (ultimate fallback)
 * 
 * INITIALIZATION: IP geolocation starts immediately when the module loads.
 * This ensures currency detection is ready before any components need it.
 * Results are cached for 24 hours to prevent rate limiting.
 */

// Currency configuration mapping - All Stripe-supported currencies
export const CURRENCY_CONFIG: Record<string, { symbol: string; label: string; text: string }> = {
    // Major Global Currencies
    usd: { symbol: '$', label: 'USD', text: 'US Dollar' },
    eur: { symbol: '€', label: 'EUR', text: 'Euro' },
    gbp: { symbol: '£', label: 'GBP', text: 'British Pound Sterling' },
    jpy: { symbol: '¥', label: 'JPY', text: 'Japanese Yen' },
    aud: { symbol: '$', label: 'AUD', text: 'Australian Dollar' },
    cad: { symbol: '$', label: 'CAD', text: 'Canadian Dollar' },
    chf: { symbol: 'CHF', label: 'CHF', text: 'Swiss Franc' },
    cny: { symbol: '¥', label: 'CNY', text: 'Chinese Yuan' },
    sek: { symbol: 'kr', label: 'SEK', text: 'Swedish Krona' },
    nzd: { symbol: '$', label: 'NZD', text: 'New Zealand Dollar' },
    mxn: { symbol: '$', label: 'MXN', text: 'Mexican Peso' },
    sgd: { symbol: 'S$', label: 'SGD', text: 'Singapore Dollar' },
    hkd: { symbol: 'HK$', label: 'HKD', text: 'Hong Kong Dollar' },
    nok: { symbol: 'kr', label: 'NOK', text: 'Norwegian Krone' },
    try: { symbol: '₺', label: 'TRY', text: 'Turkish Lira' },
    rub: { symbol: '₽', label: 'RUB', text: 'Russian Ruble' },
    inr: { symbol: '₹', label: 'INR', text: 'Indian Rupee' },
    brl: { symbol: 'R$', label: 'BRL', text: 'Brazilian Real' },
    zar: { symbol: 'R', label: 'ZAR', text: 'South African Rand' },
    krw: { symbol: '₩', label: 'KRW', text: 'South Korean Won' },
    thb: { symbol: '฿', label: 'THB', text: 'Thai Baht' },
    dkk: { symbol: 'kr', label: 'DKK', text: 'Danish Krone' },
    pln: { symbol: 'zł', label: 'PLN', text: 'Polish Złoty' },
    ils: { symbol: '₪', label: 'ILS', text: 'Israeli Shekel' },
    aed: { symbol: 'د.إ', label: 'AED', text: 'UAE Dirham' },
    sar: { symbol: 'ر.س', label: 'SAR', text: 'Saudi Riyal' },
    qar: { symbol: 'ر.ق', label: 'QAR', text: 'Qatari Riyal' },
    kwd: { symbol: 'د.ك', label: 'KWD', text: 'Kuwaiti Dinar' },
    bhd: { symbol: 'د.ب', label: 'BHD', text: 'Bahraini Dinar' },
    omr: { symbol: 'ر.ع.', label: 'OMR', text: 'Omani Rial' },
    jod: { symbol: 'د.ا', label: 'JOD', text: 'Jordanian Dinar' },
    egp: { symbol: '£', label: 'EGP', text: 'Egyptian Pound' },
    mad: { symbol: 'د.م.', label: 'MAD', text: 'Moroccan Dirham' },
    tnd: { symbol: 'د.ت', label: 'TND', text: 'Tunisian Dinar' },
    dzd: { symbol: 'د.ج', label: 'DZD', text: 'Algerian Dinar' },
    lyd: { symbol: 'ل.د', label: 'LYD', text: 'Libyan Dinar' },
    ars: { symbol: '$', label: 'ARS', text: 'Argentine Peso' },
    clp: { symbol: '$', label: 'CLP', text: 'Chilean Peso' },
    cop: { symbol: '$', label: 'COP', text: 'Colombian Peso' },
    pen: { symbol: 'S/', label: 'PEN', text: 'Peruvian Sol' },
    uyu: { symbol: '$', label: 'UYU', text: 'Uruguayan Peso' },
    pyg: { symbol: '₲', label: 'PYG', text: 'Paraguayan Guarani' },
    bob: { symbol: 'Bs', label: 'BOB', text: 'Bolivian Boliviano' },
    ngn: { symbol: '₦', label: 'NGN', text: 'Nigerian Naira' },
    kes: { symbol: 'KSh', label: 'KES', text: 'Kenyan Shilling' },
    ugx: { symbol: 'USh', label: 'UGX', text: 'Ugandan Shilling' },
    tzs: { symbol: 'TSh', label: 'TZS', text: 'Tanzanian Shilling' },
    etb: { symbol: 'Br', label: 'ETB', text: 'Ethiopian Birr' },
    ghs: { symbol: '₵', label: 'GHS', text: 'Ghanaian Cedi' },
    mwk: { symbol: 'MK', label: 'MWK', text: 'Malawian Kwacha' },
    zmw: { symbol: 'ZK', label: 'ZMW', text: 'Zambian Kwacha' },
    bwp: { symbol: 'P', label: 'BWP', text: 'Botswana Pula' },
    szl: { symbol: 'L', label: 'SZL', text: 'Swazi Lilangeni' },
    lsl: { symbol: 'L', label: 'LSL', text: 'Lesotho Loti' },
    mop: { symbol: 'MOP$', label: 'MOP', text: 'Macanese Pataca' },
    twd: { symbol: 'NT$', label: 'TWD', text: 'Taiwan Dollar' },
    bnd: { symbol: 'B$', label: 'BND', text: 'Brunei Dollar' },
    fjd: { symbol: 'FJ$', label: 'FJD', text: 'Fijian Dollar' },
    pgk: { symbol: 'K', label: 'PGK', text: 'Papua New Guinea Kina' },
    wst: { symbol: 'WS$', label: 'WST', text: 'Samoan Tala' },
    top: { symbol: 'T$', label: 'TOP', text: 'Tongan Paʻanga' },
    sbd: { symbol: 'SI$', label: 'SBD', text: 'Solomon Islands Dollar' },
    vuv: { symbol: 'Vt', label: 'VUV', text: 'Vanuatu Vatu' },
    xpf: { symbol: '₣', label: 'XPF', text: 'CFP Franc' },
    isk: { symbol: 'kr', label: 'ISK', text: 'Icelandic Krona' },
    
    // Additional Stripe-supported currencies
    afn: { symbol: '؋', label: 'AFN', text: 'Afghan Afghani' },
    all: { symbol: 'L', label: 'ALL', text: 'Albanian Lek' },
    amd: { symbol: '֏', label: 'AMD', text: 'Armenian Dram' },
    ang: { symbol: 'ƒ', label: 'ANG', text: 'Netherlands Antillean Guilder' },
    aoa: { symbol: 'Kz', label: 'AOA', text: 'Angolan Kwanza' },
    awg: { symbol: 'ƒ', label: 'AWG', text: 'Aruban Florin' },
    azn: { symbol: '₼', label: 'AZN', text: 'Azerbaijani Manat' },
    bam: { symbol: 'КМ', label: 'BAM', text: 'Bosnia-Herzegovina Convertible Mark' },
    bbd: { symbol: '$', label: 'BBD', text: 'Barbadian Dollar' },
    bdt: { symbol: '৳', label: 'BDT', text: 'Bangladeshi Taka' },
    bif: { symbol: 'FBu', label: 'BIF', text: 'Burundian Franc' },
    bmd: { symbol: '$', label: 'BMD', text: 'Bermudian Dollar' },
    bsd: { symbol: '$', label: 'BSD', text: 'Bahamian Dollar' },
    bzd: { symbol: '$', label: 'BZD', text: 'Belize Dollar' },
    cdf: { symbol: 'FC', label: 'CDF', text: 'Congolese Franc' },
    crc: { symbol: '₡', label: 'CRC', text: 'Costa Rican Colón' },
    cve: { symbol: '$', label: 'CVE', text: 'Cape Verdean Escudo' },
    czk: { symbol: 'Kč', label: 'CZK', text: 'Czech Koruna' },
    djf: { symbol: 'Fdj', label: 'DJF', text: 'Djiboutian Franc' },
    dop: { symbol: '$', label: 'DOP', text: 'Dominican Peso' },
    gel: { symbol: '₾', label: 'GEL', text: 'Georgian Lari' },
    gmd: { symbol: 'D', label: 'GMD', text: 'Gambian Dalasi' },
    gnf: { symbol: 'FG', label: 'GNF', text: 'Guinean Franc' },
    gtq: { symbol: 'Q', label: 'GTQ', text: 'Guatemalan Quetzal' },
    gyd: { symbol: '$', label: 'GYD', text: 'Guyanese Dollar' },
    hnl: { symbol: 'L', label: 'HNL', text: 'Honduran Lempira' },
    hrk: { symbol: 'kn', label: 'HRK', text: 'Croatian Kuna' },
    htg: { symbol: 'G', label: 'HTG', text: 'Haitian Gourde' },
    huf: { symbol: 'Ft', label: 'HUF', text: 'Hungarian Forint' },
    idr: { symbol: 'Rp', label: 'IDR', text: 'Indonesian Rupiah' },
    jmd: { symbol: '$', label: 'JMD', text: 'Jamaican Dollar' },
    kgs: { symbol: 'с', label: 'KGS', text: 'Kyrgyzstani Som' },
    khr: { symbol: '៛', label: 'KHR', text: 'Cambodian Riel' },
    kmf: { symbol: 'CF', label: 'KMF', text: 'Comorian Franc' },
    kyd: { symbol: '$', label: 'KYD', text: 'Cayman Islands Dollar' },
    kzt: { symbol: '₸', label: 'KZT', text: 'Kazakhstani Tenge' },
    lak: { symbol: '₭', label: 'LAK', text: 'Laotian Kip' },
    lbp: { symbol: 'ل.ل', label: 'LBP', text: 'Lebanese Pound' },
    lkr: { symbol: '₨', label: 'LKR', text: 'Sri Lankan Rupee' },
    lrd: { symbol: '$', label: 'LRD', text: 'Liberian Dollar' },
    mdl: { symbol: 'L', label: 'MDL', text: 'Moldovan Leu' },
    mga: { symbol: 'Ar', label: 'MGA', text: 'Malagasy Ariary' },
    mkd: { symbol: 'ден', label: 'MKD', text: 'Macedonian Denar' },
    mmk: { symbol: 'K', label: 'MMK', text: 'Myanmar Kyat' },
    mnt: { symbol: '₮', label: 'MNT', text: 'Mongolian Tugrik' },
    mro: { symbol: 'UM', label: 'MRO', text: 'Mauritanian Ouguiya' },
    mur: { symbol: '₨', label: 'MUR', text: 'Mauritian Rupee' },
    mvr: { symbol: 'ރ', label: 'MVR', text: 'Maldivian Rufiyaa' },
    myr: { symbol: 'RM', label: 'MYR', text: 'Malaysian Ringgit' },
    mzn: { symbol: 'MT', label: 'MZN', text: 'Mozambican Metical' },
    nad: { symbol: '$', label: 'NAD', text: 'Namibian Dollar' },
    nio: { symbol: 'C$', label: 'NIO', text: 'Nicaraguan Córdoba' },
    npr: { symbol: '₨', label: 'NPR', text: 'Nepalese Rupee' },
    pab: { symbol: 'B/.', label: 'PAB', text: 'Panamanian Balboa' },
    php: { symbol: '₱', label: 'PHP', text: 'Philippine Peso' },
    pkr: { symbol: '₨', label: 'PKR', text: 'Pakistani Rupee' },
    ron: { symbol: 'lei', label: 'RON', text: 'Romanian Leu' },
    rsd: { symbol: 'дин', label: 'RSD', text: 'Serbian Dinar' },
    rwf: { symbol: 'RF', label: 'RWF', text: 'Rwandan Franc' },
    scr: { symbol: '₨', label: 'SCR', text: 'Seychellois Rupee' },
    sll: { symbol: 'Le', label: 'SLL', text: 'Sierra Leonean Leone' },
    sos: { symbol: 'S', label: 'SOS', text: 'Somali Shilling' },
    srd: { symbol: '$', label: 'SRD', text: 'Surinamese Dollar' },
    std: { symbol: 'Db', label: 'STD', text: 'São Tomé and Príncipe Dobra' },
    syp: { symbol: '£', label: 'SYP', text: 'Syrian Pound' },
    tjs: { symbol: 'SM', label: 'TJS', text: 'Tajikistani Somoni' },
    ttd: { symbol: '$', label: 'TTD', text: 'Trinidad and Tobago Dollar' },
    uah: { symbol: '₴', label: 'UAH', text: 'Ukrainian Hryvnia' },
    uzs: { symbol: 'лв', label: 'UZS', text: 'Uzbekistani Som' },
    vef: { symbol: 'Bs', label: 'VEF', text: 'Venezuelan Bolívar' },
    vnd: { symbol: '₫', label: 'VND', text: 'Vietnamese Dong' },
    xaf: { symbol: 'FCFA', label: 'XAF', text: 'Central African CFA Franc' },
    xcd: { symbol: '$', label: 'XCD', text: 'East Caribbean Dollar' },
    xof: { symbol: 'CFA', label: 'XOF', text: 'West African CFA Franc' },
    yer: { symbol: '﷼', label: 'YER', text: 'Yemeni Rial' },
    zwl: { symbol: '$', label: 'ZWL', text: 'Zimbabwean Dollar' },
};

// Map of common locales to their default currencies
const LOCALE_TO_CURRENCY: Record<string, string> = {
    'en-US': 'USD',
    'en-GB': 'GBP',
    'en-AU': 'AUD',
    'en-NZ': 'NZD',
    'en-CA': 'CAD',
    'en-SG': 'SGD',
    'en-HK': 'HKD',
    'ja-JP': 'JPY',
    'ko-KR': 'KRW',
    'zh-CN': 'CNY',
    'id-ID': 'IDR',
    'hi-IN': 'INR',
    'ms-MY': 'MYR',
    'fil-PH': 'PHP',
    'th-TH': 'THB',
    'vi-VN': 'VND',
    'af-ZA': 'ZAR',
    // Add more mappings as needed
};

// Map of timezones to their default currencies
const TIMEZONE_TO_CURRENCY: Record<string, string> = {
    'America/New_York': 'USD',
    'America/Los_Angeles': 'USD',
    'America/Chicago': 'USD',
    'America/Denver': 'USD',
    'America/Toronto': 'CAD',
    'America/Vancouver': 'CAD',
    'Europe/London': 'GBP',
    'Europe/Paris': 'EUR',
    'Europe/Berlin': 'EUR',
    'Europe/Rome': 'EUR',
    'Europe/Madrid': 'EUR',
    'Asia/Tokyo': 'JPY',
    'Asia/Shanghai': 'CNY',
    'Asia/Seoul': 'KRW',
    'Asia/Hong_Kong': 'HKD',
    'Asia/Singapore': 'SGD',
    'Asia/Kolkata': 'INR',
    'Asia/Jakarta': 'IDR',
    'Asia/Bangkok': 'THB',
    'Asia/Manila': 'PHP',
    'Asia/Kuala_Lumpur': 'MYR',
    'Asia/Ho_Chi_Minh': 'VND',
    'Australia/Sydney': 'AUD',
    'Australia/Melbourne': 'AUD',
    'Pacific/Auckland': 'NZD',
    'Africa/Johannesburg': 'ZAR',
};

/**
 * Get currency code from IP geolocation using existing IP from Recoil state
 * @returns Promise<string> Currency code in lowercase
 */
const getCurrencyFromIP = async (): Promise<string> => {
    try {
        // Rate limiting: Don't make requests too frequently
        const now = Date.now();
        if (now - lastIPRequestTime < MIN_REQUEST_INTERVAL) {
            return 'aud';
        }
        
        lastIPRequestTime = now;
        // Get the user's IP from Recoil state
        let userIP = '';
        try {
            const currentIpState = getRecoil(ipState);
            if (currentIpState && currentIpState.ip) {
                userIP = currentIpState.ip;
            }
        } catch (recoilError) {
            // Recoil state not available yet
        }
        
        // Use external IP geolocation services
        if (userIP) {
            try {
                // Use ip-api.com (CORS-friendly and free)
                const response = await fetch(`http://ip-api.com/json/${userIP}?fields=countryCode,currency`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                    },
                });
                
                if (response.ok) {
                    const data = await response.json();
                    
                    // Get currency directly from the API response
                    const currency = data.currency?.toLowerCase();
                    
                    // Validate currency exists in our config
                    if (currency && CURRENCY_CONFIG[currency]) {
                        return currency;
                    }
                }
            } catch (apiError) {
                // API failed, continue to fallbacks
            }
        }
        
        // Fallback to browser-based detection
        try {
            const browserCurrency = getCurrencyFromEnhancedBrowser();
            if (browserCurrency && browserCurrency !== 'aud') {
                return browserCurrency;
            }
        } catch (browserError) {
            // Browser detection failed
        }
        
        return 'aud'; // Fallback if all methods fail
    } catch (error) {
        console.warn('Currency detection: IP geolocation failed:', error);
        return 'aud';
    }
};

/**
 * Get currency code from timezone
 * @returns Currency code in lowercase
 */
const getCurrencyFromTimezone = (): string => {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currency = TIMEZONE_TO_CURRENCY[timezone];
        return currency ? currency.toLowerCase() : 'aud';
    } catch (error) {
        console.warn('Timezone detection failed:', error);
        return 'aud';
    }
};

/**
 * Get currency code from enhanced browser detection
 * @returns Currency code in lowercase
 */
const getCurrencyFromEnhancedBrowser = (): string => {
    const signals: string[] = [];
    
    try {
        // 1. Browser language
        const language = navigator.language;
        signals.push(getCurrencyFromLocale(language));
        
        // 2. All browser languages
        if (navigator.languages) {
            navigator.languages.forEach(lang => {
                signals.push(getCurrencyFromLocale(lang));
            });
        }
        
        // 3. Timezone
        signals.push(getCurrencyFromTimezone());
        
        // 4. Number formatting preferences
        try {
            const numberFormat = new Intl.NumberFormat().resolvedOptions();
            if (numberFormat.locale) {
                signals.push(getCurrencyFromLocale(numberFormat.locale));
            }
        } catch (e) {
            // Ignore number format errors
        }
        
        // Count currency occurrences
        const currencyCounts = signals.reduce((acc, curr) => {
            if (curr && curr !== 'aud') { // Don't count fallback
                acc[curr] = (acc[curr] || 0) + 1;
            }
            return acc;
        }, {} as Record<string, number>);
        
        // Return most common currency or first valid one
        const sortedCurrencies = Object.entries(currencyCounts)
            .sort(([,a], [,b]) => b - a);
            
        return sortedCurrencies[0]?.[0] || 'aud';
    } catch (error) {
        console.warn('Enhanced browser detection failed:', error);
        return 'aud';
    }
};

/**
 * Get currency code from locale
 * @param locale - Browser locale string
 * @returns Currency code in lowercase
 */
export const getCurrencyFromLocale = (locale: string): string => {
    // First try exact match
    if (LOCALE_TO_CURRENCY[locale]) {
        return LOCALE_TO_CURRENCY[locale].toLowerCase();
    }

    // Try matching just the country code (e.g., "en-US" -> "US")
    const country = locale.split('-')[1];
    if (country) {
        const matchingLocale = Object.keys(LOCALE_TO_CURRENCY).find(l => l.endsWith(country));
        if (matchingLocale) {
            return LOCALE_TO_CURRENCY[matchingLocale].toLowerCase();
        }
    }

    return 'aud'; // Default fallback
};

// Global state to track initialization
let isInitialized = false;
let lastIPRequestTime = 0;
const MIN_REQUEST_INTERVAL = 10000; // Minimum 10 seconds between IP requests

// Cache keys for storage
const CURRENCY_CACHE_KEY = 'detected_currency';
const CURRENCY_CACHE_TIMESTAMP_KEY = 'detected_currency_timestamp';
const CURRENCY_COOKIE_KEY = 'user_currency';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Check if cached currency is still valid
 */
const isCachedCurrencyValid = (): boolean => {
    try {
        const timestamp = sessionStorage.getItem(CURRENCY_CACHE_TIMESTAMP_KEY);
        if (!timestamp) return false;
        
        const cacheTime = parseInt(timestamp, 10);
        const now = Date.now();
        return (now - cacheTime) < CACHE_DURATION;
    } catch (e) {
        return false;
    }
};

/**
 * Get cached currency if valid
 */
const getCachedCurrency = (): string | null => {
    try {
        // First try session storage (faster, more recent)
        if (isCachedCurrencyValid()) {
            const cachedCurrency = sessionStorage.getItem(CURRENCY_CACHE_KEY);
            if (cachedCurrency && CURRENCY_CONFIG[cachedCurrency]) {
                return cachedCurrency;
            }
        } else {
            // Clear expired session storage
            sessionStorage.removeItem(CURRENCY_CACHE_KEY);
            sessionStorage.removeItem(CURRENCY_CACHE_TIMESTAMP_KEY);
        }
        
        // Fallback to cookie storage (persistent across sessions)
        const cookieCurrency = getCookie(CURRENCY_COOKIE_KEY);
        if (cookieCurrency && CURRENCY_CONFIG[cookieCurrency]) {
            return cookieCurrency;
        }
    } catch (e) {
        console.warn('Failed to read cached currency:', e);
    }
    return null;
};

/**
 * Set cached currency with timestamp
 */
const setCachedCurrency = (currency: string): void => {
    try {
        sessionStorage.setItem(CURRENCY_CACHE_KEY, currency);
        sessionStorage.setItem(CURRENCY_CACHE_TIMESTAMP_KEY, Date.now().toString());
        
        // Also store in cookie for persistence across sessions
        setCookie(CURRENCY_COOKIE_KEY, currency, 90); // 90 days
    } catch (e) {
        console.warn('Failed to cache currency:', e);
    }
};

/**
 * Set a cookie
 */
const setCookie = (name: string, value: string, days: number): void => {
    try {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    } catch (e) {
        console.warn('Failed to set cookie:', e);
    }
};

/**
 * Get a cookie
 */
const getCookie = (name: string): string | null => {
    try {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    } catch (e) {
        console.warn('Failed to get cookie:', e);
        return null;
    }
};

/**
 * Initialize currency detection on app load
 * This runs once when the app starts to pre-populate the cache
 */
const initializeCurrencyDetection = (): void => {
    if (isInitialized) return;
    
    isInitialized = true;
    
    // Check if we already have a valid cached currency
    const existingCachedCurrency = getCachedCurrency();
    if (existingCachedCurrency) {
        return; // Don't overwrite existing cache
    }
    
    // Only proceed with IP detection if no valid cache exists
    setTimeout(() => {
        getCurrencyFromIP()
            .then(async (currency) => {
                if (currency) {
                    setCachedCurrency(currency);
                    
                    // Save detected currency to database if user doesn't have a preference
                    try {
                        const currentUserModel = getRecoil(userModelState);
                        if (currentUserModel && !currentUserModel.preferredCurrency) {
                            console.log('Currency detection: Saving detected currency to database:', currency);
                            await updateUserPreferredCurrency(currency);
                        }
                    } catch (dbError) {
                        console.warn('Currency detection: Failed to save detected currency to database:', dbError);
                    }
                }
                return currency;
            })
            .catch((error) => {
                console.warn('Currency detection: IP geolocation failed:', error);
                return 'aud';
            });
    }, 1000); // Wait 1 second for Recoil to initialize
};

/**
 * Initialize currency detection immediately
 * This runs as soon as the module is loaded
 */
initializeCurrencyDetection();

/**
 * Get user's preferred currency
 * @param userModel - The user model object
 * @returns Currency code in lowercase
 */
export const getUserPreferredCurrency = (userModel?: UserModel | null): string => {
    // 1. Check user's stored preference (highest priority)
    if (userModel?.preferredCurrency) {
        return userModel.preferredCurrency.toLowerCase();
    }
    
    // 2. Try IP geolocation (most accurate for location) - from cache
    try {
        const cachedIPCurrency = getCachedCurrency();
        if (cachedIPCurrency) {
            return cachedIPCurrency;
        }
    } catch (e) {
        // Ignore cache errors
    }
    
    // 3. Fallback to basic browser locale detection
    try {
        const browserLocale = navigator.language;
        return getCurrencyFromLocale(browserLocale);
    } catch (e) {
        console.warn('Failed to detect browser locale currency', e);
    }
    
    // 4. Try enhanced browser detection (timezone + multiple signals) as last resort
    try {
        const enhancedCurrency = getCurrencyFromEnhancedBrowser();
        if (enhancedCurrency && enhancedCurrency !== 'aud') {
            return enhancedCurrency;
        }
    } catch (e) {
        console.warn('Enhanced browser detection failed', e);
    }
    
    // 5. Ultimate fallback
    return 'aud';
};


/**
 * Get currency symbol for display
 * @param currency - Currency code
 * @returns Currency symbol
 */
export const getUserCurrencySymbol = (userModel?: UserModel | null): string => {
    const currencyCode = getUserPreferredCurrency(userModel);
    return CURRENCY_CONFIG[currencyCode]?.symbol || '$';
};

/**
 * Get currency label (e.g., "USD", "AUD")
 * @param userModel - The user model object
 * @returns Currency label in uppercase
 */
export const getUserCurrencyLabel = (userModel?: UserModel | null): string => {
    const currencyCode = getUserPreferredCurrency(userModel);
    return CURRENCY_CONFIG[currencyCode]?.label || 'AUD';
};

/**
 * Get full currency name
 * @param userModel - The user model object
 * @returns Full currency name (e.g., "US Dollar")
 */
export const getUserCurrencyText = (userModel?: UserModel | null): string => {
    const currencyCode = getUserPreferredCurrency(userModel);
    return CURRENCY_CONFIG[currencyCode]?.text || 'Australian Dollar';
};

/**
 * Get list of all available currencies
 * @returns Array of currency objects with code, symbol, label, and text
 */
export const getAvailableCurrencies = () => {
    return Object.entries(CURRENCY_CONFIG).map(([code, details]) => ({
        code,
        ...details
    }));
};

/**
 * Update user's preferred currency
 * @param currencyCode - The currency code to set (e.g., 'usd', 'aud')
 * @returns Updated UserModel or null for logged-out users
 * @throws Error if update fails
 */
export const updateUserPreferredCurrency = async (currencyCode: string): Promise<UserModel | null> => {
    try {
        // Validate currency code
        const normalizedCode = currencyCode.toLowerCase();
        if (!CURRENCY_CONFIG[normalizedCode]) {
            throw new Error(`Invalid currency code: ${currencyCode}`);
        }

        // Check if user is authenticated
        let isAuthenticated = false;
        try {
            await Auth.currentSession();
            isAuthenticated = true;
        } catch (authError) {
            // User is not authenticated
            isAuthenticated = false;
        }

        if (isAuthenticated) {
            // User is logged in - update database
            const userSession = await Auth.currentSession();
            const accessToken = userSession.getAccessToken().getJwtToken();

            // Call API to update user currency
            const response = await callApi(
                "post",
                `${process.env.REACT_APP_ENV}-anystay-user`,
                "user/currency",
                {
                    headers: {
                        Authorization: accessToken,
                    },
                    body: {
                        preferredCurrency: normalizedCode
                    }
                }
            );

            // Merge API response with existing user model to avoid dropping fields like profilePicture/identityID
            const previousUserModel = getRecoil(userModelState);
            const apiUserModel = (response && response.body && response.body.userModel) as UserModel | undefined;
            const mergedUserModel: UserModel = {
                ...(previousUserModel || ({} as UserModel)),
                ...(apiUserModel || ({} as UserModel)),
                preferredCurrency: normalizedCode,
            };
            setRecoil(userModelState, mergedUserModel);

            // Update cache and cookies with the new currency preference
            setCachedCurrency(normalizedCode);

            return mergedUserModel;
        } else {
            // User is logged out - only update cache
            setCachedCurrency(normalizedCode);
            
            // For logged-out users, we can't update the userModel state since there's no user
            // The currency will be picked up from cache on next page load
            return null;
        }
    } catch (error) {
        console.error('Failed to update user currency preference:', error);
        throw error;
    }
};
